/**
 * Life events calculations and type guards
 * @module domain/retirement/lifeEvents
 */

import { 
  LifeEventDirection, 
  LifeEventFrequency, 
  LifeEventKind, 
  SellType,
  EscalationType,
  AssetCategory,
  CashFlowSubtype,
  EndYearMode,
} from "./constants";
import type { 
  LifeEvent, 
  CashFlowEvent, 
  BuyAssetEvent, 
  SellAssetEvent,
  LoanEvent,
  LifeEventsImpactResult,
  SellAssetRequest,
  CategoryBalances,
  ProductBalance,
  BuyAssetRequest,
} from "./types";
import { addPurchaseToProducts, createSyntheticProduct } from "./portfolio";

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if a life event is a cash flow event
 */
export function isCashFlowEvent(event: LifeEvent): event is CashFlowEvent {
  return event.kind === LifeEventKind.CASHFLOW || !("kind" in event) || event.kind === undefined;
}

/**
 * Type guard to check if a life event is a buy asset event
 */
export function isBuyAssetEvent(event: LifeEvent): event is BuyAssetEvent {
  return event.kind === LifeEventKind.BUY_ASSET;
}

/**
 * Type guard to check if a life event is a sell asset event
 */
export function isSellAssetEvent(event: LifeEvent): event is SellAssetEvent {
  return event.kind === LifeEventKind.SELL_ASSET;
}

/**
 * Type guard to check if a life event is a loan event
 */
export function isLoanEvent(event: LifeEvent): event is LoanEvent {
  return event.kind === LifeEventKind.LOAN;
}

// ============================================================================
// LOAN CALCULATION HELPERS
// ============================================================================

/**
 * Standard PMT (Payment) formula for fixed-rate amortization (Shpitzer).
 * M = P * [r(1+r)^n] / [(1+r)^n - 1]
 *
 * @param principal - Loan amount
 * @param annualInterestRate - Annual interest rate as percentage (e.g. 4.5 for 4.5%)
 * @param months - Total loan period in months
 * @returns Monthly payment amount
 */
export function calculateLoanMonthlyPayment(
  principal: number,
  annualInterestRate: number,
  months: number,
): number {
  if (!principal || !months || months <= 0) return 0;

  if (!annualInterestRate || annualInterestRate === 0) {
    return principal / months;
  }

  const monthlyRate = annualInterestRate / 100 / 12;
  const factor = Math.pow(1 + monthlyRate, months);
  return (principal * (monthlyRate * factor)) / (factor - 1);
}

/**
 * Calculate total repayment for a loan (principal + interest).
 * totalRepayment = monthlyPayment * periodMonths
 */
export function calculateLoanTotalRepayment(
  principal: number,
  annualInterestRate: number,
  months: number,
): number {
  const monthlyPayment = calculateLoanMonthlyPayment(principal, annualInterestRate, months);
  return monthlyPayment * months;
}

// ============================================================================
// LEGACY SUPPORT
// ============================================================================

/**
 * Legacy life event format (before kind was added)
 */
interface LegacyLifeEvent {
  id: string;
  name: string;
  startYear: number;
  endYear?: number;
  frequency: LifeEventFrequency;
  amount: number;
  direction: LifeEventDirection;
  categoryTag?: string;
  enabled: boolean;
  notes?: string;
  isAutoGenerated?: boolean;
}

/**
 * Migrate a legacy life event to the new format
 */
export function migrateLifeEvent(event: LegacyLifeEvent | LifeEvent): LifeEvent {
  // Already in new format
  if ("kind" in event && event.kind !== undefined) {
    return event as LifeEvent;
  }
  
  // Migrate legacy format to cashflow event
  const legacy = event as LegacyLifeEvent;
  return {
    kind: LifeEventKind.CASHFLOW,
    id: legacy.id,
    name: legacy.name,
    startYear: legacy.startYear,
    endYear: legacy.endYear,
    frequency: legacy.frequency,
    amount: legacy.amount,
    direction: legacy.direction,
    categoryTag: legacy.categoryTag,
    enabled: legacy.enabled,
    notes: legacy.notes,
  };
}

// ============================================================================
// ID GENERATION
// ============================================================================

/**
 * Generate a unique ID for life events
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// EVENT CLASSIFICATION HELPERS
// ============================================================================

/**
 * Determine whether a life event is recurring.
 * An event is recurring if its frequency is monthly or yearly,
 * regardless of whether an explicit endYear is set (no endYear = repeats indefinitely).
 */
export function isRecurringEvent(event: LifeEvent): boolean {
  return event.frequency !== LifeEventFrequency.ONE_TIME;
}

/**
 * Check whether a cashflow event is income-related (increase or reduce income).
 * Income-related events default to ending at retirement rather than life expectancy,
 * since income stops at retirement.
 */
export function isIncomeRelatedEvent(event: LifeEvent): boolean {
  if (!isCashFlowEvent(event)) return false;
  const subtype = (event as CashFlowEvent).subtype;
  return (
    subtype === CashFlowSubtype.INCREASE_INCOME ||
    subtype === CashFlowSubtype.REDUCE_INCOME
  );
}

// ============================================================================
// EFFECTIVE END YEAR
// ============================================================================

/**
 * Milestone years needed by `getEffectiveEndYear` and `normalizeLifeEventEndYears`.
 */
export interface MilestoneYears {
  /** Calendar year at which the user reaches life expectancy */
  lifeExpectancyYear: number;
  /** Calendar year at which the user reaches target (early) retirement age */
  retirementYear: number;
  /** Calendar year at which pension becomes eligible (67 males / 65 females in Israel) */
  pensionYear: number;
}

/**
 * Get the effective end year of a single event.
 *
 * Resolution order:
 * 1. If `endYearMode` is set to a milestone mode, the corresponding milestone year is used.
 * 2. If `endYearMode` is SPECIFIC_YEAR (or absent — legacy) **and** an explicit endYear is set,
 *    that explicit value is used.
 * 3. For recurring events without an explicit endYear (legacy data), the auto-detection
 *    heuristic applies: income-related events → retirement, all others → life expectancy.
 * 4. One-time events simply return their startYear.
 */
export function getEffectiveEndYear(
  event: LifeEvent,
  lifeExpectancyYear: number,
  retirementYear: number,
  pensionYear?: number,
  partnerRetirementYear?: number,
  partnerPensionYear?: number,
): number {
  const mode = event.endYearMode;

  // For income events attributed to the second partner, "retirement" refers to
  // the partner's own retirement year when available.
  const isPartnerIncomeEvent =
    isCashFlowEvent(event) &&
    event.owner === "partner" &&
    isIncomeRelatedEvent(event);
  const effectiveRetirementYear =
    partnerRetirementYear != null && isPartnerIncomeEvent
      ? partnerRetirementYear
      : retirementYear;
  const effectivePensionYear =
    partnerPensionYear != null && isPartnerIncomeEvent
      ? partnerPensionYear
      : (pensionYear ?? retirementYear);

  // Explicit milestone modes always win
  if (mode === EndYearMode.AT_RETIREMENT) {
    return Math.max(event.startYear + 1, effectiveRetirementYear);
  }
  if (mode === EndYearMode.AT_PENSION) {
    return Math.max(event.startYear + 1, effectivePensionYear);
  }
  if (mode === EndYearMode.AT_LIFE_EXPECTANCY) {
    return Math.max(event.startYear + 1, lifeExpectancyYear);
  }

  // SPECIFIC_YEAR (or absent / legacy): use the explicit endYear if present
  if (event.endYear != null && event.endYear > event.startYear) {
    return event.endYear;
  }

  // Legacy heuristic for recurring events without an explicit endYear
  if (isRecurringEvent(event)) {
    const fallback = isIncomeRelatedEvent(event) ? effectiveRetirementYear : lifeExpectancyYear;
    return Math.max(event.startYear + 1, fallback);
  }

  return event.startYear;
}

// ============================================================================
// EVENT NORMALIZATION
// ============================================================================

/**
 * Normalize life events by resolving their effective end years according to
 * `endYearMode` and the provided milestone years.
 *
 * Returns a new array with shallow-cloned events (does not mutate the originals).
 */
export function normalizeLifeEventEndYears(
  events: LifeEvent[],
  retirementYear: number,
  lifeExpectancyYear: number,
  pensionYear?: number,
  partnerRetirementYear?: number,
  partnerPensionYear?: number,
): LifeEvent[] {
  return events.map((event) => {
    // One-time events are left untouched
    if (event.frequency === LifeEventFrequency.ONE_TIME) {
      return event;
    }

    // If the event has an explicit milestone mode other than SPECIFIC_YEAR, resolve it
    const mode = event.endYearMode;
    if (mode && mode !== EndYearMode.SPECIFIC_YEAR) {
      const effectiveEnd = getEffectiveEndYear(
        event,
        lifeExpectancyYear,
        retirementYear,
        pensionYear,
        partnerRetirementYear,
        partnerPensionYear,
      );
      return { ...event, endYear: effectiveEnd };
    }

    // SPECIFIC_YEAR with an explicit endYear — nothing to resolve
    if (event.endYear != null && event.endYear > event.startYear) {
      return event;
    }

    // Legacy: recurring event without endYear or mode — use heuristic
    const effectiveEnd = getEffectiveEndYear(
      event,
      lifeExpectancyYear,
      retirementYear,
      pensionYear,
      partnerRetirementYear,
      partnerPensionYear,
    );
    return { ...event, endYear: effectiveEnd };
  });
}

// ============================================================================
// IMPACT CALCULATIONS
// ============================================================================

/**
 * Calculate event amount based on frequency for a given year.
 * For recurring cashflow events, applies escalation (percentage or fixed) based on years since start.
 * For loan events, returns the annual repayment (monthly payment × 12).
 */
function calculateEventAmount(event: LifeEvent, year: number): number {
  const isActive = year >= event.startYear && (!event.endYear || year <= event.endYear);
  if (!isActive) return 0;

  // Loan events use their own calculation — monthly payment * 12
  if (isLoanEvent(event)) {
    const monthlyPayment = calculateLoanMonthlyPayment(
      event.loanAmount,
      event.interestRate,
      event.periodMonths,
    );
    return monthlyPayment * 12;
  }

  // Get base amount per occurrence
  let baseAmount: number;
  switch (event.frequency) {
    case LifeEventFrequency.ONE_TIME:
      return year === event.startYear ? event.amount : 0;
    case LifeEventFrequency.YEARLY:
      baseAmount = event.amount;
      break;
    case LifeEventFrequency.MONTHLY:
      baseAmount = event.amount * 12;
      break;
    default:
      return 0;
  }

  // Apply escalation for recurring cashflow events
  if (isCashFlowEvent(event) && event.escalationType && event.escalationType !== EscalationType.NONE) {
    const yearsSinceStart = year - event.startYear;
    if (yearsSinceStart > 0) {
      if (event.escalationType === EscalationType.PERCENTAGE && event.escalationRate) {
        // Compound percentage: amount * (1 + rate)^years
        baseAmount = baseAmount * Math.pow(1 + event.escalationRate, yearsSinceStart);
      } else if (event.escalationType === EscalationType.FIXED && event.escalationAmount) {
        // Fixed increase: amount + (fixedAmount * years)
        // For monthly events, escalationAmount is per-month, so multiply by 12
        const annualEscalation = event.frequency === LifeEventFrequency.MONTHLY
          ? event.escalationAmount * 12
          : event.escalationAmount;
        baseAmount = baseAmount + annualEscalation * yearsSinceStart;
      }
    }
  }

  return Math.max(0, baseAmount);
}

/**
 * Calculate life events impact for a given year (legacy compatibility)
 * Returns simple cashflow impact number
 */
export function calculateLifeEventsImpact(
  lifeEvents: LifeEvent[],
  year: number
): number {
  const result = calculateDetailedLifeEventsImpact(lifeEvents, year);
  return result.netImpact;
}

/**
 * Calculate detailed life events impact including buy asset and sell asset events
 */
export function calculateDetailedLifeEventsImpact(
  lifeEvents: LifeEvent[],
  year: number
): LifeEventsImpactResult {
  const result: LifeEventsImpactResult = {
    netImpact: 0,
    inflows: 0,
    outflows: 0,
    spendingReduction: 0,
    incomeReduction: 0,
    incomeAdjustments: {
      primary: { reduction: 0, increase: 0 },
      partner: { reduction: 0, increase: 0 },
      household: { reduction: 0, increase: 0 },
    },
    savingsReduction: 0,
    directPurchases: {} as Record<AssetCategory, number>,
    directPurchasesDetailed: [],
    sellRequests: [],
    buyFromCreditRequests: [],
    creditFromInflows: 0,
  };

  for (const event of lifeEvents) {
    if (!event.enabled) continue;

    const eventAmount = calculateEventAmount(event, year);
    if (eventAmount === 0 && !isSellAssetEvent(event)) continue;

    // Handle based on event kind
    if (isCashFlowEvent(event)) {
      // The bucket an income event affects: a specific partner or the whole
      // household (legacy behavior when no owner is set).
      const ownerBucket = result.incomeAdjustments[event.owner ?? "household"];
      // Cash flow event - affects overall portfolio
      if (event.direction === LifeEventDirection.OUTFLOW && event.subtype === CashFlowSubtype.REDUCE_INCOME) {
        // REDUCE_INCOME is treated as reduced income, not as an expense.
        // It does NOT withdraw from the portfolio; instead it lowers effective savings.
        result.incomeReduction += eventAmount;
        ownerBucket.reduction += eventAmount;
      } else if (event.direction === LifeEventDirection.OUTFLOW) {
        result.netImpact -= eventAmount;
        result.outflows += eventAmount;
      } else if (event.subtype === CashFlowSubtype.REDUCE_EXPENSES) {
        // REDUCE_EXPENSES is treated as reduced spending, not as income.
        // It does NOT add money to the portfolio; instead it lowers annual spend.
        result.spendingReduction += eventAmount;
      } else {
        result.netImpact += eventAmount;
        result.inflows += eventAmount;
        // Inflows also add to buying credit
        result.creditFromInflows += eventAmount;
        // INCREASE_INCOME additionally raises the owner's ongoing pension
        // contributions (contributions are a percentage of salary).
        if (event.subtype === CashFlowSubtype.INCREASE_INCOME) {
          ownerBucket.increase += eventAmount;
        }
      }
    } else if (isBuyAssetEvent(event)) {
      // Buy asset event — all purchases come from buying credit (= CASH pool)
      // regardless of investFrom value (EXTRA, CASHFLOW, or BUYING_CREDIT)
      const category = event.category;

      result.buyFromCreditRequests.push({
        category,
        amount: eventAmount,
        customReturnAssumption: event.customReturnAssumption,
        name: event.name,
        eventId: event.id,
      });
    } else if (isLoanEvent(event)) {
      // Loan disbursement: in the start year of a NEW (non-auto-generated) loan,
      // the principal is received as a one-time cash inflow. This models the fact
      // that when you take a loan/mortgage, you receive the money.
      // Auto-generated loan events (from existing debts) skip this because
      // the money was already received and spent in the past.
      if (!event.isAutoGenerated && year === event.startYear && event.loanAmount > 0) {
        result.netImpact += event.loanAmount;
        result.inflows += event.loanAmount;
        result.creditFromInflows += event.loanAmount;
      }

      if (event.includedInExpenses) {
        // This loan's payments are already baked into the user's base monthlySpend
        // (sourced from cashflow data). We must NOT add them again as outflows.
        //
        // However, once the loan ends the user's real spending drops.
        // We detect "ended" as: year > endYear (the event is no longer active).
        // calculateEventAmount already returned 0 for inactive events, so if we
        // reach this point the event is still active → nothing to do.
        //
        // The spending reduction for ENDED includedInExpenses loans is handled in
        // a second pass below (outside the main loop) so we can catch events that
        // are no longer active in the current year.
        // (no-op while active — payments are already in monthlySpend)
      } else {
        // Regular loan event — treat as outflow (monthly repayments)
        result.netImpact -= eventAmount;
        result.outflows += eventAmount;
      }
    } else if (isSellAssetEvent(event)) {
      // Sell asset event - will be processed to get actual amount based on category balance
      const isActive = year >= event.startYear && (!event.endYear || year <= event.endYear);
      if (!isActive) continue;
      
      // For one-time, only trigger in start year; for recurring, trigger every year
      if (event.frequency === LifeEventFrequency.ONE_TIME && year !== event.startYear) {
        continue;
      }

      result.sellRequests.push({
        category: event.category,
        productId: event.productId,
        sellType: event.sellType,
        amount: event.sellAmount,
        percentage: event.sellPercentage,
      });
    }
  }

  // Second pass: for includedInExpenses loan events that have ENDED by this year,
  // add a spendingReduction equal to their annual payment amount.
  // This reflects that the user's real expenses drop once they stop paying the loan.
  for (const event of lifeEvents) {
    if (!event.enabled) continue;
    if (!isLoanEvent(event) || !event.includedInExpenses) continue;

    // The loan has ended if year is past its endYear
    const hasEnded = event.endYear != null && year > event.endYear;
    if (!hasEnded) continue;

    // Calculate the annual payment this loan used to cost
    const annualPayment = calculateLoanMonthlyPayment(
      event.loanAmount,
      event.interestRate,
      event.periodMonths,
    ) * 12;

    if (annualPayment > 0) {
      result.spendingReduction += annualPayment;
    }
  }

  return result;
}

// ============================================================================
// SELL/BUY PROCESSING
// ============================================================================

/**
 * Process sell requests: removes from the sold category and adds proceeds to CASH.
 * Returns the total proceeds amount (for tracking/display purposes).
 */
export function processSellRequests(
  sellRequests: SellAssetRequest[],
  categoryBalances: CategoryBalances
): number {
  let totalProceeds = 0;

  for (const request of sellRequests) {
    const currentBalance = categoryBalances[request.category] || 0;
    if (currentBalance <= 0) continue;

    let sellAmount: number;
    if (request.sellType === SellType.PERCENTAGE) {
      sellAmount = currentBalance * (request.percentage || 0) / 100;
    } else {
      sellAmount = Math.min(request.amount || 0, currentBalance);
    }

    if (sellAmount > 0) {
      categoryBalances[request.category] -= sellAmount;
      // Proceeds go to CASH — money stays in the portfolio
      categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + sellAmount;
      totalProceeds += sellAmount;
    }
  }

  return totalProceeds;
}

/**
 * Process buy requests that use buying credit (= CASH pool).
 * Withdraws from CASH and adds to target category.
 * Returns amount spent and unspent credit.
 */
export function processBuyFromCredit(
  buyRequests: { category: AssetCategory; amount: number }[],
  categoryBalances: CategoryBalances,
  availableCredit: number
): { spent: number; unspent: number } {
  let remainingCredit = availableCredit;
  let totalSpent = 0;

  for (const request of buyRequests) {
    const amountToSpend = Math.min(request.amount, remainingCredit);
    if (amountToSpend > 0) {
      categoryBalances[request.category] = (categoryBalances[request.category] || 0) + amountToSpend;
      // Withdraw from CASH — buying credit is the CASH pool
      categoryBalances[AssetCategory.CASH] = Math.max(0,
        (categoryBalances[AssetCategory.CASH] || 0) - amountToSpend);
      remainingCredit -= amountToSpend;
      totalSpent += amountToSpend;
    }
  }

  return { spent: totalSpent, unspent: remainingCredit };
}

// ============================================================================
// PRODUCT-LEVEL SELL/BUY PROCESSING
// ============================================================================

/**
 * Process sell requests from product balances
 * If a productId is specified, sells only from that specific product.
 * Otherwise, sells proportionally from all products in the specified category.
 * Proceeds are added as a CASH product so money stays in the portfolio.
 * Returns the total proceeds amount (for tracking/display purposes).
 */
export function processSellRequestsFromProducts(
  sellRequests: SellAssetRequest[],
  products: ProductBalance[]
): number {
  let totalProceeds = 0;

  for (const request of sellRequests) {
    // If a specific product is targeted, sell only from that product
    if (request.productId != null) {
      const targetProduct = products.find(
        p => String(p.productId) === String(request.productId) && p.balance > 0
      );
      if (!targetProduct) continue;

      let sellAmount: number;
      if (request.sellType === SellType.PERCENTAGE) {
        sellAmount = targetProduct.balance * (request.percentage || 0) / 100;
      } else {
        sellAmount = Math.min(request.amount || 0, targetProduct.balance);
      }

      if (sellAmount > 0) {
        targetProduct.balance -= sellAmount;
        // Proceeds go to a CASH product — money stays in the portfolio
        products.push(createSyntheticProduct(
          "תמורת מכירה",
          AssetCategory.CASH,
          sellAmount
        ));
        totalProceeds += sellAmount;
      }
      continue;
    }

    // No specific product — sell proportionally from all products in category
    const categoryProducts = products.filter(p => p.category === request.category && p.balance > 0);
    const categoryTotal = categoryProducts.reduce((sum, p) => sum + p.balance, 0);
    
    if (categoryTotal <= 0) continue;

    let sellAmount: number;
    if (request.sellType === SellType.PERCENTAGE) {
      sellAmount = categoryTotal * (request.percentage || 0) / 100;
    } else {
      sellAmount = Math.min(request.amount || 0, categoryTotal);
    }

    if (sellAmount > 0) {
      // Sell proportionally from each product in the category
      const sellRatio = sellAmount / categoryTotal;
      for (const product of categoryProducts) {
        const productSell = product.balance * sellRatio;
        product.balance -= productSell;
      }
      // Proceeds go to a CASH product — money stays in the portfolio
      products.push(createSyntheticProduct(
        "תמורת מכירה",
        AssetCategory.CASH,
        sellAmount
      ));
      totalProceeds += sellAmount;
    }
  }

  return totalProceeds;
}

/**
 * Process buy requests that use buying credit (= CASH pool), adding to product balances.
 * Withdraws from CASH products and creates synthetic products for purchases.
 * Returns amount spent and unspent credit.
 */
export function processBuyFromCreditToProducts(
  buyRequests: BuyAssetRequest[],
  products: ProductBalance[],
  availableCredit: number
): { spent: number; unspent: number } {
  let remainingCredit = availableCredit;
  let totalSpent = 0;

  for (const request of buyRequests) {
    const amountToSpend = Math.min(request.amount, remainingCredit);
    if (amountToSpend > 0) {
      addPurchaseToProducts(
        products,
        request.category,
        amountToSpend,
        request.name,
        request.customReturnAssumption,
        request.eventId
      );
      // Withdraw from CASH products — buying credit is the CASH pool
      let remaining = amountToSpend;
      const cashProducts = products.filter(p => p.category === AssetCategory.CASH && p.balance > 0);
      for (const cashProduct of cashProducts) {
        const take = Math.min(remaining, cashProduct.balance);
        cashProduct.balance -= take;
        remaining -= take;
        if (remaining <= 0) break;
      }
      remainingCredit -= amountToSpend;
      totalSpent += amountToSpend;
    }
  }

  return { spent: totalSpent, unspent: remainingCredit };
}
